import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Cloud, FileSpreadsheet, Folder, Globe, HardDrive, Link2, Link2Off, RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "../../api/client";
import { myFilesApi, type MyDrive, type MyItem, type MySite, type OneDriveStatus, type PersonalDataset } from "../../api/myFiles";
import { Badge, Banner, Button, Card, EmptyState, Field, Input, Modal, SkeletonRows, formatRelative, useToast } from "../../ds";

const RESULT_TEXT: Record<string, [("success" | "warning" | "danger"), string]> = {
  connected: ["success", "Your Microsoft account is connected."],
  cancelled: ["warning", "The connection was cancelled. Nothing was saved."],
  invalid_state: ["danger", "That sign-in link expired. Please press Connect again."],
  exchange_failed: ["danger", "Microsoft did not accept the sign-in. Please try again."],
  tenant_not_allowed: ["danger", "That Microsoft account belongs to an organisation that is not approved."],
  different_account: ["danger", "Please connect the same Microsoft account you signed in to this platform with."],
  tenant_not_available: ["danger", "OneDrive / SharePoint is not available for your organisation yet."],
  account_disabled: ["danger", "This account is disabled."],
};

/** Status query shared by the Data page tab (to hide it when the feature is off) and the panel itself. */
export function useOneDriveStatus() {
  return useQuery({ queryKey: ["me", "onedrive"], queryFn: myFilesApi.status, staleTime: 30_000 });
}

export function MyFilesPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const status = useOneDriveStatus();
  const [params, setParams] = useSearchParams();
  const result = params.get("onedrive");

  useEffect(() => {
    if (result) qc.invalidateQueries({ queryKey: ["me", "onedrive"] });
  }, [result, qc]);

  const disconnect = useMutation({
    mutationFn: myFilesApi.disconnect,
    onSuccess: () => { toast.success("Disconnected", "Your Microsoft connection was removed from this platform."); qc.invalidateQueries({ queryKey: ["me", "onedrive"] }); },
    onError: (e) => toast.error("Couldn't disconnect", e instanceof Error ? e.message : undefined),
  });

  if (status.isLoading) return <Card><SkeletonRows rows={4} /></Card>;
  if (status.isError || !status.data) return <Banner tone="danger" title="Couldn't load your connection">{status.error instanceof Error ? status.error.message : null}</Banner>;
  const s = status.data;
  if (s.state === "disabled") return null;
  const note = result ? RESULT_TEXT[result] : undefined;

  return (
    <div className="ds-stack" style={{ gap: 16 }}>
      {note ? (
        <Banner tone={note[0]} action={<Button size="sm" onClick={() => { params.delete("onedrive"); setParams(params, { replace: true }); }}>Dismiss</Button>}>{note[1]}</Banner>
      ) : null}
      <StatusCard status={s} onDisconnect={() => disconnect.mutate()} disconnecting={disconnect.isPending} />
      {s.state === "connected" ? <Browser /> : null}
      {s.state === "connected" || s.state === "needs_reconnect" ? <MyDatasets reconnect={s.state === "needs_reconnect"} /> : null}
    </div>
  );
}

function StatusCard({ status, onDisconnect, disconnecting }: { status: OneDriveStatus; onDisconnect: () => void; disconnecting: boolean }) {
  const connect = () => { window.location.href = myFilesApi.connectUrl; };
  if (status.state === "not_available_for_your_tenant") {
    return <Banner tone="info" title="Not available for your organisation yet">{status.message}</Banner>;
  }
  return (
    <Card>
      <div className="ds-row" style={{ alignItems: "center", gap: 14 }}>
        <span className="ds-stat__icon" style={{ width: 40, height: 40 }}><Cloud size={20} /></span>
        <div className="ds-grow" style={{ minWidth: 0 }}>
          {status.state === "connected" ? (
            <>
              <div style={{ fontWeight: 650 }}>Connected as {status.account ?? "your Microsoft account"}</div>
              <div className="ds-small ds-muted">
                You see only the files and sites you already have permission to open.
                {status.last_used_at ? ` Last used ${formatRelative(status.last_used_at)}.` : ""}
              </div>
            </>
          ) : status.state === "needs_reconnect" ? (
            <>
              <div style={{ fontWeight: 650 }}>Reconnect your Microsoft account</div>
              <div className="ds-small ds-muted">{status.message ?? "Microsoft asked you to sign in again."} Your datasets keep showing their last data until you do.</div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 650 }}>Use your own OneDrive and SharePoint files</div>
              <div className="ds-small ds-muted">Connect your Microsoft account. The platform can only read what you can already open, and never changes anything.</div>
            </>
          )}
        </div>
        {status.state === "connected" ? <Badge tone="success" dot>Connected</Badge> : null}
        {status.state === "needs_reconnect" ? <Badge tone="warning" dot>Needs attention</Badge> : null}
        {status.state === "connected" || status.state === "needs_reconnect" ? (
          <Button icon={Link2Off} loading={disconnecting} onClick={onDisconnect}>Disconnect</Button>
        ) : null}
        {status.state !== "connected" ? <Button variant="primary" icon={status.state === "needs_reconnect" ? RefreshCw : Link2} onClick={connect}>{status.state === "needs_reconnect" ? "Reconnect" : "Connect"}</Button> : null}
      </div>
    </Card>
  );
}

type Crumb = { label: string; driveId: string; itemId?: string };
type Root = { kind: "drive"; drive: MyDrive } | { kind: "site"; site: MySite };

function Browser() {
  const toast = useToast();
  const qc = useQueryClient();
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [siteQuery, setSiteQuery] = useState("");
  const [sites, setSites] = useState<MySite[] | null>(null);
  const [siteDrives, setSiteDrives] = useState<{ site: MySite; drives: MyDrive[] } | null>(null);
  const [pick, setPick] = useState<{ file: MyItem; driveId: string } | null>(null);
  const [searchErr, setSearchErr] = useState<string | null>(null);

  const drive = useQuery({ queryKey: ["me", "onedrive", "drive"], queryFn: myFilesApi.drive });
  const followed = useQuery({ queryKey: ["me", "onedrive", "followed"], queryFn: myFilesApi.followedSites });
  const current = crumbs[crumbs.length - 1];
  const listing = useQuery({
    queryKey: ["me", "onedrive", "children", current?.driveId, current?.itemId],
    queryFn: () => myFilesApi.children(current!.driveId, current!.itemId),
    enabled: !!current,
  });

  const start = (root: Root) => {
    if (root.kind === "drive") setCrumbs([{ label: "My OneDrive", driveId: root.drive.id }]);
    else setSiteDrives({ site: root.site, drives: [] });
  };
  const openSite = async (site: MySite) => {
    try {
      setSiteDrives({ site, drives: await myFilesApi.siteDrives(site.id) });
    } catch (e) {
      toast.error("Couldn't open that site", e instanceof Error ? e.message : undefined);
    }
  };
  const search = async () => {
    setSearchErr(null);
    try {
      setSites(await myFilesApi.searchSites(siteQuery));
    } catch (e) {
      setSearchErr(e instanceof Error ? e.message : "Search failed");
    }
  };

  return (
    <Card title="Browse" subtitle="Only files and sites you have permission to open appear here. Pick an Excel (.xlsx) file to use as data.">
      <div className="ds-grid ds-grid--2" style={{ alignItems: "start" }}>
        <div className="ds-stack">
          <div className="ds-small ds-faint">Start from</div>
          <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
            <div className="ds-list__row is-clickable" onClick={() => drive.data && start({ kind: "drive", drive: drive.data })}>
              <HardDrive size={16} className="ds-faint" />
              <div className="ds-list__main"><div className="ds-list__title">My OneDrive</div></div>
              <ChevronRight size={16} className="ds-faint" />
            </div>
            {(followed.data ?? []).map((site) => (
              <div key={site.id} className="ds-list__row is-clickable" onClick={() => void openSite(site)}>
                <Globe size={16} className="ds-faint" />
                <div className="ds-list__main"><div className="ds-list__title">{site.name}</div><div className="ds-list__meta">Followed site</div></div>
                <ChevronRight size={16} className="ds-faint" />
              </div>
            ))}
          </div>
          <div className="ds-row">
            <div className="ds-input-wrap ds-grow"><Search size={15} /><input className="ds-input" placeholder="Find another SharePoint site" value={siteQuery} onChange={(e) => setSiteQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void search()} /></div>
            <Button onClick={() => void search()}>Search</Button>
          </div>
          {searchErr ? <div className="ds-error-text">{searchErr}</div> : null}
          {sites ? (
            sites.length === 0 ? <div className="ds-small ds-muted">No sites found that you can open.</div> : (
              <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10, maxHeight: 220, overflow: "auto" }}>
                {sites.map((site) => (
                  <div key={site.id} className="ds-list__row is-clickable" onClick={() => void openSite(site)}>
                    <Globe size={16} className="ds-faint" />
                    <div className="ds-list__main"><div className="ds-list__title">{site.name}</div><div className="ds-list__meta ds-truncate">{site.webUrl}</div></div>
                  </div>
                ))}
              </div>
            )
          ) : null}
        </div>

        <div className="ds-stack">
          {siteDrives && !current ? (
            <>
              <div className="ds-small ds-faint">{siteDrives.site.name} — libraries</div>
              <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
                {siteDrives.drives.map((d) => (
                  <div key={d.id} className="ds-list__row is-clickable" onClick={() => setCrumbs([{ label: siteDrives.site.name, driveId: d.id }, ])}>
                    <Folder size={16} className="ds-faint" />
                    <div className="ds-list__main"><div className="ds-list__title">{d.name}</div></div>
                    <ChevronRight size={16} className="ds-faint" />
                  </div>
                ))}
                {siteDrives.drives.length === 0 ? <div className="ds-list__row ds-muted">Loading or no libraries you can open.</div> : null}
              </div>
            </>
          ) : null}
          {current ? (
            <>
              <div className="ds-row ds-small" style={{ flexWrap: "wrap", gap: 4 }}>
                {crumbs.map((c, i) => (
                  <span key={i}>
                    <button type="button" className="ds-link" onClick={() => setCrumbs(crumbs.slice(0, i + 1))}>{c.label}</button>
                    {i < crumbs.length - 1 ? " / " : ""}
                  </span>
                ))}
                <button type="button" className="ds-link" style={{ marginLeft: "auto" }} onClick={() => { setCrumbs([]); setSiteDrives(null); }}>Close</button>
              </div>
              {listing.isLoading ? <SkeletonRows rows={5} /> : listing.isError ? (
                <Banner tone="danger" title="Couldn't open that folder">{listing.error instanceof Error ? listing.error.message : null}</Banner>
              ) : (
                <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10, maxHeight: 380, overflow: "auto" }}>
                  {(listing.data?.items ?? []).map((item) =>
                    item.isFolder ? (
                      <div key={item.id} className="ds-list__row is-clickable" onClick={() => setCrumbs([...crumbs, { label: item.name, driveId: current.driveId, itemId: item.id }])}>
                        <Folder size={16} color="var(--warning)" />
                        <div className="ds-list__main"><div className="ds-list__title">{item.name}</div></div>
                        <ChevronRight size={16} className="ds-faint" />
                      </div>
                    ) : (
                      <div key={item.id} className={`ds-list__row${item.isExcel ? " is-clickable" : ""}`} style={item.isExcel ? undefined : { opacity: 0.5 }} onClick={() => item.isExcel && setPick({ file: item, driveId: item.driveId || current.driveId })} title={item.isExcel ? "Use this file" : "Only .xlsx files can be used"}>
                        <FileSpreadsheet size={16} color={item.isExcel ? "var(--success)" : undefined} className={item.isExcel ? undefined : "ds-faint"} />
                        <div className="ds-list__main"><div className="ds-list__title">{item.name}</div></div>
                      </div>
                    ),
                  )}
                  {listing.data && listing.data.items.length === 0 ? <div className="ds-list__row ds-muted">This folder is empty.</div> : null}
                  {listing.data?.truncated ? <div className="ds-list__row ds-muted">Only the first items are shown.</div> : null}
                </div>
              )}
            </>
          ) : !siteDrives ? (
            <EmptyState icon={Cloud} title="Pick a place to browse" description="Choose My OneDrive or one of your SharePoint sites on the left." />
          ) : null}
        </div>
      </div>
      <UseFileDialog pick={pick} onClose={() => setPick(null)} onCreated={() => { setPick(null); qc.invalidateQueries({ queryKey: ["me", "onedrive", "datasets"] }); qc.invalidateQueries({ queryKey: ["admin", "datasets"] }); toast.success("Dataset created", "It is ready to use in the designer."); }} />
    </Card>
  );
}

function UseFileDialog({ pick, onClose, onCreated }: { pick: { file: MyItem; driveId: string } | null; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [sheet, setSheet] = useState("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (pick) { setName(pick.file.name.replace(/\.xlsx$/i, "")); setSheet(""); setErr(null); } }, [pick]);
  const sheets = useQuery({
    queryKey: ["me", "onedrive", "sheets", pick?.driveId, pick?.file.id],
    queryFn: () => myFilesApi.sheets(pick!.driveId, pick!.file.id),
    enabled: !!pick,
  });
  const choices = (sheets.data?.objects ?? []).filter((o) => !o.hidden);
  useEffect(() => { if (!sheet && choices.length) setSheet(choices[0].type === "table" ? `table:${choices[0].name}` : choices[0].name); }, [choices, sheet]);
  const create = useMutation({
    mutationFn: () => myFilesApi.create({ name, drive_id: pick!.driveId, item_id: pick!.file.id, sheet_name: sheet || null }),
    onSuccess: onCreated,
    onError: (e) => setErr(e instanceof ApiError || e instanceof Error ? e.message : "Couldn't create the dataset"),
  });
  return (
    <Modal open={!!pick} onClose={onClose} title="Use this file" description={pick?.file.name}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={create.isPending} disabled={!name.trim() || sheets.isLoading || !!sheets.error} onClick={() => create.mutate()}>Use this file</Button></>}>
      <div className="ds-stack">
        <Banner tone="info">This is a personal file. It refreshes with <strong>your</strong> Microsoft access, so if you disconnect or lose access the data stops updating (the last data stays).</Banner>
        <Field label="Dataset name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Sheet or table" error={sheets.error instanceof Error ? sheets.error.message : undefined}>
          {sheets.isLoading ? <div className="ds-small ds-muted">Reading the workbook…</div> : (
            <select className="ds-input" value={sheet} onChange={(e) => setSheet(e.target.value)}>
              {choices.map((o) => <option key={`${o.type}:${o.name}`} value={o.type === "table" ? `table:${o.name}` : o.name}>{o.type === "table" ? `Table: ${o.name}` : o.name}{o.row_count ? ` (${o.row_count} rows)` : ""}</option>)}
            </select>
          )}
        </Field>
        {err ? <div className="ds-error-text">{err}</div> : null}
      </div>
    </Modal>
  );
}

function MyDatasets({ reconnect }: { reconnect: boolean }) {
  const list = useQuery({ queryKey: ["me", "onedrive", "datasets"], queryFn: myFilesApi.datasets });
  const items: PersonalDataset[] = list.data ?? [];
  if (!items.length && !list.isLoading) return null;
  return (
    <Card title="My personal-file datasets" subtitle="Built on files from your own OneDrive / SharePoint">
      {list.isLoading ? <SkeletonRows rows={3} /> : (
        <div className="ds-list">
          {items.map((d) => (
            <div key={d.id} className="ds-list__row">
              <FileSpreadsheet size={16} className="ds-faint" />
              <div className="ds-list__main">
                <div className="ds-list__title">{d.name}</div>
                <div className="ds-list__meta ds-truncate">{d.source_name}</div>
              </div>
              <Badge tone="info">Personal file</Badge>
              {d.needs_attention || reconnect ? <Badge tone="warning" dot title={d.attention_reason ?? undefined}>Needs attention</Badge> : null}
              {d.needs_attention ? <Button size="sm" icon={RefreshCw} onClick={() => { window.location.href = myFilesApi.connectUrl; }}>Reconnect</Button> : null}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function MyFilesPage() {
  const status = useOneDriveStatus();
  if (status.data && status.data.state === "disabled") return null;
  return (
    <div className="ds-page">
      <h1 style={{ margin: "0 0 4px", fontSize: 22 }}>My OneDrive &amp; SharePoint</h1>
      <p className="ds-muted" style={{ margin: "0 0 18px" }}>Use Excel files you already have access to as report data. Nobody else's files are shown, and nothing is ever changed in them.</p>
      <MyFilesPanel />
    </div>
  );
}
